import {
  Intent,
  MenuItem,
  PopoverPosition,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { createSelector } from '@reduxjs/toolkit';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';
import { SelectItem, stringToSelectItem } from 'vet-bones/utils/select';

import { refreshDatabase } from 'src/actions/databases/action-creators/refreshDatabase';
import { TEST_IDS, getDatabaseItemTestId } from 'src/constants/testIds';
import { catalogDatabaseSelector } from 'src/selectors/catalogDatabase';
import { databaseItemsSelector } from 'src/selectors/databaseItems';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import { querylogDatabaseSelector } from 'src/selectors/querylogDatabase';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import {
  INVALID_NAME_MATCHER,
  NAME_STARTS_WITH_MATCHER,
} from 'src/utils/removeInvalidNameChars';

const createNewDatabaseSelectItemRenderer =
  (invalidDatabases: Set<string>, allowDuplicates = false) =>
  (items: SelectItem[], testId?: string) =>
  // eslint-disable-next-line react/display-name
  (
    query: string,
    active: boolean,
    handleClick: React.MouseEventHandler<HTMLElement>
  ) => {
    // eslint-disable-next-line react/destructuring-assignment
    const trimmedQuery = query.trim();

    if (
      !trimmedQuery ||
      (!allowDuplicates &&
        items.some(
          (item) => item.text.toLowerCase() === trimmedQuery.toLowerCase()
        ))
    ) {
      return undefined;
    }

    // validate new database name
    let errorMessage;
    const invalidChars = trimmedQuery.match(INVALID_NAME_MATCHER);

    if (!trimmedQuery.match(NAME_STARTS_WITH_MATCHER)) {
      errorMessage = copy.components.databaseSelector.errorMessages.startsWith;
    } else if (invalidChars) {
      errorMessage = copy.components.databaseSelector.errorMessages.characters(
        uniq(invalidChars)
      );
    } else if (invalidDatabases.has(trimmedQuery)) {
      errorMessage = copy.components.databaseSelector.errorMessages.catalog;
    }

    if (errorMessage) {
      return (
        <MenuItem
          className="sd-menu-item-no-results"
          data-testid={TEST_IDS.database.selectorCreateItemError}
          intent={Intent.DANGER}
          multiline
          text={errorMessage}
        />
      );
    }

    return (
      <MenuItem
        active={active}
        data-testid={testId}
        icon={IconNames.ADD}
        onClick={handleClick}
        shouldDismissPopover={false}
        text={copy.components.defaults.itemSelectorCreate(trimmedQuery)}
      />
    );
  };

export type DatabaseSelectorProps = {
  creatable?: boolean;
  databaseId: string;
  disabled?: boolean;
  popoverPosition?: PopoverPosition;
  selectorRef?: React.Ref<ItemSelector<null, SelectItem, SelectItem | null>>;
  setDatabaseId: (id: string) => void;
};

export const DatabaseSelector: React.VFC<DatabaseSelectorProps> = ({
  creatable,
  databaseId,
  disabled,
  popoverPosition,
  selectorRef,
  setDatabaseId,
}) => {
  const dispatch = useReduxDispatch();
  const selectedDatabase = React.useMemo(
    () => (databaseId ? stringToSelectItem(databaseId) : null),
    [databaseId]
  );

  const databaseItems = useReduxSelector(databaseItemsSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);
  const isDatabaseReady = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const invalidDbs = useReduxSelector(
    createSelector(
      catalogDatabaseSelector,
      querylogDatabaseSelector,
      (catalogDatabase, querylogDatabase) =>
        new Set([catalogDatabase, querylogDatabase])
    )
  );

  const usableDatabaseItems = React.useMemo(
    () => databaseItems.filter((item) => !invalidDbs.has(item.value)),
    [databaseItems, invalidDbs]
  );

  React.useEffect(() => {
    if (!isConnected) {
      setDatabaseId('');
    }
  }, [isConnected, setDatabaseId]);

  const isSelectorDisabled = !isConnected || isRefreshingDatabases || disabled;

  const isLoading =
    isRefreshingDatabases ||
    (databaseId &&
      usableDatabaseItems.find(({ value }) => value === databaseId) &&
      !isDatabaseReady);

  const handleSelectDatabaseId = (
    item: SelectItem | null,
    evt?: React.MouseEvent
  ) => {
    const nextDatabaseId = item?.value || '';
    stopPropagation(evt);
    setDatabaseId(nextDatabaseId);
    if (nextDatabaseId) {
      dispatch(refreshDatabase(nextDatabaseId));
    }
  };

  return (
    <ItemSelector.Single
      buttonIcon={icons.database}
      buttonIconColor={databaseId ? colors.darkIcon : undefined}
      buttonRightIcon={
        isLoading ? <Spinner size={SpinnerSize.SMALL} /> : undefined
      }
      buttonText={
        databaseId ||
        (creatable
          ? copy.components.databaseSelector.creatablePlaceholder
          : copy.components.databaseSelector.placeholder)
      }
      creatable={creatable}
      createNewItemRenderer={
        creatable ? createNewDatabaseSelectItemRenderer(invalidDbs) : undefined
      }
      createNewItemTestId={TEST_IDS.database.selectorCreateItem}
      disabled={isSelectorDisabled}
      filterOrCreateText={copy.components.defaults.filterOrCreate}
      filterTestId={TEST_IDS.database.selectorFilter}
      filterText={copy.components.defaults.filter}
      items={usableDatabaseItems}
      itemTestId={getDatabaseItemTestId}
      onButtonClick={stopPropagation}
      onClear={stopPropagation}
      onSelect={handleSelectDatabaseId}
      popoverProps={{
        backdropProps: { onClick: stopPropagation },
        position: popoverPosition,
      }}
      resetOnClose
      selected={selectedDatabase}
      selectorRef={selectorRef}
      testId={TEST_IDS.database.selector}
    />
  );
};
